/**
 * TaskErrorBoundary Component
 *
 * Error boundary for Task Management feature.
 * Catches JavaScript errors in child components and displays
 * a fallback UI instead of crashing the entire application.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { space, fontSize, fontWeight, radius, neutral, feedback } from '../../configs/designTokens';
import { Result, Button } from 'antd';

class TaskErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console (could be sent to error reporting service)
    console.error('TaskErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    // Call optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });

    // Call optional onReset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Result
          status="error"
          title={this.props.title || 'Something went wrong'}
          subTitle={
            this.props.subTitle ||
            'An error occurred while loading this section. Please try again.'
          }
          extra={[
            <Button key="retry" onClick={this.handleReset}>
              Try Again
            </Button>,
            <Button key="refresh" type="primary" onClick={this.handleRefresh}>
              Refresh Page
            </Button>,
          ]}
        >
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <div
              style={{
                marginTop: space[6],
                padding: space[4],
                backgroundColor: feedback.errorBg,
                border: `1px solid ${feedback.errorBorder}`,
                borderRadius: radius.xs,
                textAlign: 'left',
              }}
            >
              <details style={{ whiteSpace: 'pre-wrap' }}>
                <summary style={{ cursor: 'pointer', fontWeight: fontWeight.semibold, color: feedback.errorText }}>
                  Error Details (Development Only)
                </summary>
                <pre style={{ marginTop: space[2], fontSize: fontSize.caption, color: neutral[700] }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            </div>
          )}
        </Result>
      );
    }

    return this.props.children;
  }
}

TaskErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  onError: PropTypes.func,
  onReset: PropTypes.func,
};

TaskErrorBoundary.defaultProps = {
  fallback: null,
  title: null,
  subTitle: null,
  onError: null,
  onReset: null,
};

export default TaskErrorBoundary;
